import config from '@/utils/ApiUtils'
import { fetchWithToken } from '@/utils/Keycloak'
import { TypeUtils } from '@/utils/ErrorUtils'
import { denormalizeProductType } from '@/utils/ProductTypeColors'
import { getExcelValue } from '@/utils/parserConstants'


const parserArgs = 'dedupe=true&lowerCase=true&clean=false'
export default {
  filterHeaderFromBackendData(data, mappedColumns) {
    if (!Array.isArray(data) || data.length === 0) {
      return data;
    }

    const firstRow = data[0];
    if (!firstRow || !TypeUtils.isPlainObject(firstRow)) {
      return data;
    }

    const availableColumns = mappedColumns || [];
    const columnNames = (() => {
      if (Array.isArray(availableColumns)) {
        return availableColumns.map(col => col.name || col).filter(name => name);
      }
      if (TypeUtils.isPlainObject(availableColumns)) {
        return Object.values(availableColumns).concat(Object.keys(availableColumns)).filter(name => name && TypeUtils.isString(name));
      }
      return [];
    })();
    
    
    // Obtenir les valeurs de la première ligne (EAN, PLU, NOM, etc.)
    const firstRowValues = [
      ...(firstRow.EAN !== undefined ? [String(firstRow.EAN).trim()] : []),
      ...(firstRow.PLU !== undefined ? [String(firstRow.PLU).trim()] : []),
      ...(firstRow.NOM !== undefined ? [String(firstRow.NOM).trim()] : [])
    ];
    
    // Fallback: prendre toutes les valeurs de l'objet si on n'a pas EAN/PLU/NOM
    const effectiveRowValues = firstRowValues.length > 0
      ? firstRowValues
      : Object.values(firstRow).filter(v => v != null).map(val => String(val).trim());
    
    // Vérifier si les valeurs correspondent aux noms de colonnes (colonnes mappées ou noms typiques)
    const headerPatterns = ['ean', 'plu', 'nom', 'libelle', 'code', 'reference', 'barcode', 'name'];
    const allColumnsToCheck = [...columnNames, ...headerPatterns];
    
    const matchingColumns = effectiveRowValues.filter(value => 
      allColumnsToCheck.some(col => 
        col.toLowerCase() === value.toLowerCase() ||
        value.toLowerCase().includes(col.toLowerCase()) ||
        col.toLowerCase().includes(value.toLowerCase())
      )
    );
    
    
    // Vérification alternative : patterns typiques d'en-têtes
    const headerMatches = effectiveRowValues.filter(value => 
      headerPatterns.some(pattern => 
        value.toLowerCase().includes(pattern) || pattern.includes(value.toLowerCase())
      )
    );

    // Si plus de la moitié des valeurs correspondent à des noms de colonnes, c'est probablement un en-tête
    const isHeaderRow = (matchingColumns.length >= Math.ceil(effectiveRowValues.length / 2) && effectiveRowValues.length > 0) || headerMatches.length >= 2;
    
    // Retourner les données sans la ligne d'en-tête si elle est détectée
    if (isHeaderRow && data.length > 1) {
      const filteredData = data.slice(1); // Enlever la première ligne
      return filteredData;
    } else {
      return data;
    }
  },

  uploadFile(file) {
    // Upload classique pour tous les fichiers (Excel ou non)
    const data = new FormData();
    data.append('file', file);
    return fetchWithToken(config().parserExcelV2Backend + '/upload', {
      method: 'POST',
      body: data
    }).then((response) => response.json());
  },
  parseExcel({ fileUuidOrFile, mappedColumns, clientId, xlsxUuid = null }) {
    const requestPromise = this._buildExcelRequest(fileUuidOrFile, mappedColumns, clientId, xlsxUuid);

    return requestPromise
      .then(response => response.json())
      .then(result => this._processExcelResult(result, mappedColumns));
  },

  _buildExcelRequest(fileUuidOrFile, mappedColumns, clientId, xlsxUuid) {
    if (fileUuidOrFile instanceof File) {
      const formData = new FormData();
      formData.append('file', fileUuidOrFile);
      formData.append('idXlsx', xlsxUuid || '');
      formData.append('clientId', clientId.toString());
      // Note: Backend expects 'collumns' (typo) - do not change without backend update
      formData.append('collumns', JSON.stringify(mappedColumns));

      return fetchWithToken(config().parserExcelV2Backend + '/parse', {
        method: 'POST',
        body: formData
      });
    }

    const payload = {
      idXlsx: fileUuidOrFile,
      // Note: Backend expects 'collumns' (typo) - do not change without backend update
      collumns: mappedColumns,
      clientId: clientId
    };

    return fetchWithToken(config().parserExcelV2Backend + '/parse', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  },

  _processExcelResult(result, mappedColumns) {
    const extracted = this._extractExcelMetadata(result);
    const filteredData = Array.isArray(extracted.data) && extracted.data.length > 0
      ? this.filterHeaderFromBackendData(extracted.data, mappedColumns)
      : extracted.data;

    return {
      data: filteredData,
      idParsing: extracted.idParsing,
      idXlsx: extracted.idXlsx,
      integrationSuccess: extracted.integrationSuccess
    };
  },

  _extractExcelMetadata(result) {
    if (result && TypeUtils.isPlainObject(result) && result.data && Array.isArray(result.data)) {
      return {
        data: result.data,
        idParsing: result.pgLine?.idParsing || null,
        idXlsx: result.pgLine?.idXlsx || null,
        integrationSuccess: result.integrationSuccess
      };
    }

    if (Array.isArray(result)) {
      return {
        data: result,
        idParsing: Date.now().toString(),
        idXlsx: null,
        integrationSuccess: false
      };
    }

    return { data: [], idParsing: null, idXlsx: null, integrationSuccess: null };
  },
  /**
   * Met les lignes validees au format attendu par parser-v2-results : nom/EAN/PLU
   * ramenes sur les cles normalisees, type de produit denormalise.
   * Partage par la creation de base et par la generation de mise a jour.
   */
  consolidateLines(parserLines, columnMapping) {
    const filteredLines = parserLines.filter((line) => {
      const { chosenResult } = line;
      return line.deleted === false || chosenResult.idPoisson !== null;
    });

    return filteredLines.map((line) => {
      const excelName = getExcelValue(line.excelRow, 'NOM', columnMapping);
      const excelEan = getExcelValue(line.excelRow, 'EAN', columnMapping);
      const excelPlu = getExcelValue(line.excelRow, 'PLU', columnMapping);
      const normalizedExcelRow = { ...line.excelRow, NOM: excelName, EAN: excelEan, PLU: excelPlu };
      const result = { ...line.chosenResult, text: excelName, excelRow: normalizedExcelRow };
      if (result.metadata?.productType) {
        result.metadata = { ...result.metadata, productType: denormalizeProductType(result.metadata.productType) };
      }
      if (line.updateAction) {
        result.updateAction = line.updateAction;
      }
      return result;
    });
  },

  /**
   * Produits d'une base client existante (codes magasin avec leur composition, produit
   * LS et recette, fumaisons, plateaux) : sert a savoir, ligne par ligne, si un produit
   * du fichier Excel est un ajout, une modification ou deja identique en base.
   */
  getClientDatabaseSnapshot(databaseUuid) {
    return fetchWithToken(`${config().parserResultsV2Backend}/database/client/${databaseUuid}/codes`, {
      method: 'GET'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then((response) => ({
        codes: response.codes || [],
        fumaisons: response.fumaisons || [],
        plateaux: response.plateaux || []
      }));
  },

  /**
   * Construit le script SQLite de mise a jour ciblee a partir de la base choisie.
   * Ne modifie rien : le script est relu puis envoye depuis l'editeur SQL.
   */
  generateUpdateScript({ clientId, serial, databaseUuid, version, versionTable, parserLines, columnMapping }) {
    const payload = {
      clientId: String(clientId),
      serial: serial || '',
      databaseUuid,
      version,
      ...(versionTable ? { versionTable } : {}),
      lines: this.consolidateLines(parserLines, columnMapping)
    };

    return fetchWithToken(`${config().parserResultsV2Backend}/database/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((errorText) => {
            throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
          });
        }
        return response.json();
      });
  },
  createDatabase(clientId, serial, parserLines, columnMapping) {
    const payload = {
      clientId,
      serial,
      lines: this.consolidateLines(parserLines, columnMapping)
    };
    
    return fetchWithToken(config().parserResultsV2Backend + '/database', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error('[createDatabase] Erreur détectée:', error);
        console.error('[createDatabase] Stack trace:', error.stack);
        throw error;
      });
  },
  parseTexts(idXlsx = null, clientId = null, excelData, onProgress = null) {
    const payload = this._buildBatchPayload({ idXlsx, clientId, excelData });

    return fetchWithToken(config().parserV2Backend + '/parse/batch?' + parserArgs, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .then(response => this._handleBatchResponse(response, onProgress))
      .catch(err => {
        console.error('Error while loading parser response', err);
        throw err;
      });
  },

  _buildBatchPayload({ idXlsx, clientId, excelData }) {
    if (!excelData || !excelData.rows || !excelData.textColumn) {
      throw new Error('excelData with rows and textColumn is required');
    }

    const payload = {
      excelData: excelData
    };
    if (idXlsx) payload.idXlsx = idXlsx;
    if (clientId) payload.clientId = clientId;
    return payload;
  },

  _handleBatchResponse(response, onProgress = null) {
    if (response && response.jobId) {
      return this._pollJobUntilComplete(response.jobId, { onProgress });
    }
    if (Array.isArray(response)) {
      return {
        results: this._processParseResults(response),
        excelData: null,
        idXlsx: null,
        clientId: null
      };
    }
    if (response && response.error) {
      return Promise.reject(new Error(`Backend error: ${response.message || response.error}`));
    }
    return Promise.reject(new Error('Invalid backend response'));
  },

  _pollJobUntilComplete(jobId, options = {}) {
    // Timeout large (60 min) : le parsing passe chaque ligne dans un LLM, donc les
    // très gros fichiers (plusieurs milliers de lignes) peuvent dépasser 10 min.
    // Le job vit de toute façon côté serveur ; ce timeout ne borne que l'attente du navigateur.
    const { pollInterval = 3000, maxRetries = 1200, timeout = 3600000, onProgress } = options;
    const startTime = Date.now();
    let retries = 0;
    let accumulatedResults = [];

    const poll = () => {
      if (retries >= maxRetries) {
        return Promise.reject(new Error(`Polling max retries exceeded (${maxRetries} retries)`));
      }
      if (Date.now() - startTime > timeout) {
        const elapsedMinutes = Math.floor((Date.now() - startTime) / 60000);
        return Promise.reject(new Error(`Polling timeout exceeded (${elapsedMinutes} minutes)`));
      }
      retries++;

      // Poll with result=false to avoid fetching large result payloads
      return this.getJobStatus(jobId, { count: 10, result: false })
        .then(jobStatus => {
          // Process partial results if available (from SSE or other sources)
          if (jobStatus.results && Array.isArray(jobStatus.results) && jobStatus.results.length > 0) {
            const processedPartialResults = this._processParseResults(jobStatus.results);
            
            // Merge new results with accumulated results
            // Assuming results array index corresponds to the original text index
            processedPartialResults.forEach((result, index) => {
              if (result && result.length > 0) {
                accumulatedResults[index] = result;
              }
            });
          }

          // Call progress callback with both progress info and partial results
          if (onProgress && typeof onProgress === 'function') {
            onProgress({
              processedCount: jobStatus.processedCount || 0,
              totalCount: jobStatus.totalCount || 0,
              status: jobStatus.status || 'processing',
              partialResults: [...accumulatedResults] // Send a copy of accumulated results
            });
          }

          if (jobStatus.status === 'completed') {
            // Fetch final results with result=true
            return this.getJobStatus(jobId, { count: 10, result: true })
              .then(jobWithResults => {
                const finalResults = jobWithResults.results && jobWithResults.results.length > 0
                  ? this._processParseResults(jobWithResults.results)
                  : accumulatedResults;
                return {
                  results: finalResults,
                  excelData: jobWithResults.excelData || null,
                  idXlsx: jobWithResults.idXlsx || null,
                  clientId: jobWithResults.clientId || null
                };
              });
          }
          if (jobStatus.status === 'failed') {
            return Promise.reject(new Error(jobStatus.error || 'Batch job failed'));
          }

          // Continue polling
          return new Promise(resolve => setTimeout(resolve, pollInterval))
            .then(() => poll());
        });
    };

    return poll();
  },

  /**
   * Process and dedupe parse results
   * @private
   */
  _processParseResults(responses) {
    if (!Array.isArray(responses)) {
      return [];
    }
    
    const processedResults = responses.map((response) => {
      if (!Array.isArray(response)) return response;
      const uniqueResults = response.filter((result, index, self) => {
        return self.findIndex(t => 
          t.metadata?.idPoisson === result.metadata?.idPoisson && 
          t.metadata?.idConditionnement === result.metadata?.idConditionnement
        ) === index
      })
      return uniqueResults
    });
    
    return processedResults;
  },
  
  parseText(text) {
    const payload = {
      text: text
    }
    return fetchWithToken(config().parserV2Backend + '/parse?' + parserArgs, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    }).then((response) => response.json())
  },
  startParser({ fileUuidOrFile, mappedColumns, clientId, storeData = null, onProgress = null }) {
    if (storeData && Array.isArray(storeData) && storeData.length > 0) {
      return this._parseFromStoreData(storeData, fileUuidOrFile, clientId, mappedColumns, onProgress);
    }

    if (fileUuidOrFile instanceof File) {
      return this._parseFromFile(fileUuidOrFile, mappedColumns, clientId, onProgress);
    }

    return this._parseFromUuid(fileUuidOrFile, mappedColumns, clientId, onProgress);
  },

  _parseFromStoreData(storeData, fileUuidOrFile, clientId, mappedColumns, onProgress = null) {
    // Build excelData structure for persistence (includes textColumn for backend)
    const excelData = this._buildExcelData(storeData, mappedColumns);

    if (!excelData) {
      return Promise.reject(new Error('Failed to build excelData from storeData'));
    }

    return this.parseTexts(fileUuidOrFile, clientId, excelData, onProgress)
      .then(parserResponse => {
        // Handle new response format: { results, excelData, idXlsx, clientId }
        const results = parserResponse.results || parserResponse || [];
        return {
          parsedExcel: storeData,
          parsedTexts: results,
          idParsing: parserResponse?.idParsing || null,
          idXlsx: fileUuidOrFile,
          excelData: excelData
        };
      });
  },

  _buildExcelData(storeData, mappedColumns) {
    if (!Array.isArray(storeData) || storeData.length === 0) {
      return null;
    }

    // Extract headers from first row keys
    const firstRow = storeData[0];
    const headers = Object.keys(firstRow);

    // Normalize mappedColumns to { field: columnKey } format
    const mapping = {};
    if (mappedColumns) {
      if (typeof mappedColumns === 'object' && !Array.isArray(mappedColumns)) {
        // Object format: { EAN: 'A', PLU: 'B', NOM: 'C' }
        Object.assign(mapping, mappedColumns);
      } else if (Array.isArray(mappedColumns)) {
        // Array format: [{ name: 'EAN', nameFile: 'A' }, ...]
        mappedColumns.forEach(col => {
          if (col.name && col.nameFile) {
            mapping[col.name] = col.nameFile;
          }
        });
      }
    }

    // Determine textColumn - doit être une clé RÉELLEMENT présente dans headers,
    // sinon parser-v2 renvoie 500 ("textColumn not found in headers").
    // - chemin "UUID" (parser-v2-excel) : lignes normalisées => headers = ['EAN','PLU','NOM'] => 'NOM'
    // - chemin "storeData" (fichier lu côté front) : headers = noms d'origine => mapping.NOM (ex. 'Libellé')
    const mappedNom = mapping.NOM || mapping.nom;
    const textColumn = headers.includes('NOM')
      ? 'NOM'
      : (mappedNom && headers.includes(mappedNom))
        ? mappedNom
        : headers[0];

    // Stringify all row values - backend requires all values as strings
    const stringifiedRows = storeData.map(row => {
      const stringifiedRow = {};
      headers.forEach(header => {
        const value = row[header];
        stringifiedRow[header] = value != null ? String(value) : '';
      });
      return stringifiedRow;
    });

    return {
      headers: headers,
      rows: stringifiedRows,
      mapping: mapping,
      textColumn: textColumn
    };
  },

  _parseFromFile(file, mappedColumns, clientId, onProgress = null) {
    return this.uploadFile(file)
      .then(uploadResponse => {
        if (uploadResponse.error) {
          throw new Error(`Upload error: ${uploadResponse.error}`);
        }
        return this.parseExcel({ fileUuidOrFile: uploadResponse.uuid, mappedColumns, clientId });
      })
      .then(response => this._processParseExcelResponse(response, clientId, mappedColumns, onProgress));
  },

  _parseFromUuid(fileUuidOrFile, mappedColumns, clientId, onProgress = null) {
    return this.parseExcel({ fileUuidOrFile, mappedColumns, clientId })
      .then(response => this._processParseExcelResponse(response, clientId, mappedColumns, onProgress));
  },

  _processParseExcelResponse(response, clientId, mappedColumns, onProgress = null) {
    const { data: excelData, idParsing, idXlsx, integrationSuccess } = this._extractResponseData(response);
    const fileIdentifier = idXlsx || null;

    // Build excelData structure for persistence (includes textColumn for backend)
    const excelDataForPersistence = this._buildExcelData(excelData, mappedColumns);

    if (!excelDataForPersistence) {
      return Promise.reject(new Error('Failed to build excelData from parsed Excel response'));
    }

    return this.parseTexts(fileIdentifier, clientId, excelDataForPersistence, onProgress)
      .then(parserResponse => {
        // Handle new response format: { results, excelData, idXlsx, clientId }
        const results = parserResponse.results || parserResponse || [];
        return {
          parsedExcel: excelData,
          parsedTexts: results,
          idParsing: idParsing || parserResponse?.idParsing || null,
          idXlsx: idXlsx,
          integrationSuccess: integrationSuccess,
          excelData: excelDataForPersistence
        };
      })
      .catch(error => {
        console.error('[startParser] Error in parseTexts:', error);
        return {
          parsedExcel: excelData,
          parsedTexts: [],
          idParsing: idParsing,
          idXlsx: idXlsx,
          integrationSuccess: integrationSuccess,
          excelData: excelDataForPersistence
        };
      });
  },

  _extractResponseData(response) {
    if (response && response.data && Array.isArray(response.data)) {
      return {
        data: response.data,
        idParsing: response.idParsing || response.pgLine?.idParsing || null,
        idXlsx: response.idXlsx || response.pgLine?.idXlsx || null,
        integrationSuccess: response.integrationSuccess
      };
    }
    if (Array.isArray(response)) {
      return { data: response, idParsing: null, idXlsx: null, integrationSuccess: null };
    }
    return { data: [], idParsing: null, idXlsx: null, integrationSuccess: null };
  },

  getJobsByIdXlsx(idXlsx) {
    return fetchWithToken(`${config().parserV2Backend}/parse/batch/by-xlsx/${idXlsx}`, {})
      .then(response => response.json())
      .then(data => data.jobIds || [])
  },

  getJobStatus(jobId, options = {}) {
    const { count = 10, short = false, result = false } = options
    const params = new URLSearchParams({ 
      count: String(count), 
      short: String(short),
      result: String(result)
    })
    return fetchWithToken(`${config().parserV2Backend}/parse/batch/${jobId}?${params}`, {})
      .then(response => response.json())
  },


}